import { useTranslation } from 'react-i18next';
import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckPointInfosManagement } from '../components/CheckPointInfosManagement';
import {
  useGetCheckPointById,
  useUpdateCheckPoint,
} from '@/hooks/check-point.hooks';
import {
  checkPointSchema,
  TCheckPointSchema,
} from '@/schemas/check-point/check-point.schema';
import { TabItem } from '@/components/Tab/TabItem';
import { TabList } from '@/components/Tab/TabList';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { CheckPointConfigsManagement } from '../components/CheckPointConfigsManagement';
import { useGetTab } from '@/hooks/tab.hooks';
import { EditCheckPointTab } from '@/type/ui/tab/checkPointTab';
import { getLanguage, getTranslatedItem } from '@/utils/language.utils';

export const EditCheckPointPage = () => {
  const { id } = useParams();
  const { t } = useTranslation('checkPoint');
  const { data, isLoading } = useGetCheckPointById(`${id}`);
  const navigate = useNavigate();

  const translatedCheckPoint = getTranslatedItem(
    data?.data?.translatedCheckPoints,
  );

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditCheckPointTab.INFOS,
  });

  const {
    watch: watchCheckPoint,
    control: checkPointControl,
    register: registerCheckPoint,
    setValue: setCheckPointValue,
    setError: setCheckPointError,
    handleSubmit: handleSubmitCheckPoint,
    formState: {
      errors: checkPointErrors,
      isSubmitting: isSubmittingCheckPoint,
    },
  } = useForm<TCheckPointSchema>({
    resolver: zodResolver(checkPointSchema),
  });

  const {
    mutate: mutateUpdateCheckPoint,
    isPending: isPendingUpdateCheckPoint,
  } = useUpdateCheckPoint({
    t,
    handleSuccess: () => navigate('/check-points'),
  });

  const onSubmitCheckPointInfos = (checkPoint: TCheckPointSchema) => {
    const { languageCode } = getLanguage();
    const dataWithLanguageCode = {
      ...checkPoint,
      languageCode,
    };

    const checkPointWithId = { id, ...dataWithLanguageCode };
    mutateUpdateCheckPoint(checkPointWithId);
  };

  return (
    <>
      <PageHeader
        backToLink="/check-points"
        title={t('theCheckPoint')}
        subTitle={translatedCheckPoint?.name || data?.data?.name}
        subTitleIconClassName="ki-abstract-20"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('checkPointList')} linkTo="/check-points" />
            <BreadcrumbItem
              text={translatedCheckPoint?.name || data?.data?.name}
              active
            />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('checkPointNotFound')}...`} />
      ) : (
        <div>
          <TabList className="mb-5">
            <TabItem
              text={t('common:informations')}
              leftIcon={<i className="ki-outline ki-badge" />}
              active={currentTab === EditCheckPointTab.INFOS}
              onChange={() => navigateToTab(EditCheckPointTab.INFOS)}
            />
            <TabItem
              text={t('common:setting')}
              leftIcon={<i className="ki-outline ki-setting-4" />}
              active={currentTab === EditCheckPointTab.CONFIGS}
              onChange={() => navigateToTab(EditCheckPointTab.CONFIGS)}
            />
          </TabList>
          {currentTab === EditCheckPointTab.INFOS && (
            <CheckPointInfosManagement<TCheckPointSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchCheckPoint}
              control={checkPointControl}
              register={registerCheckPoint}
              setValue={setCheckPointValue}
              setError={setCheckPointError}
              errors={checkPointErrors}
              submitDisabled={
                isSubmittingCheckPoint || isPendingUpdateCheckPoint
              }
              onSubmit={handleSubmitCheckPoint(onSubmitCheckPointInfos)}
            />
          )}
          {currentTab === EditCheckPointTab.CONFIGS && (
            <CheckPointConfigsManagement<TCheckPointSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchCheckPoint}
              control={checkPointControl}
              register={registerCheckPoint}
              setValue={setCheckPointValue}
              setError={setCheckPointError}
              errors={checkPointErrors}
              submitDisabled={
                isSubmittingCheckPoint || isPendingUpdateCheckPoint
              }
              onSubmit={handleSubmitCheckPoint(onSubmitCheckPointInfos)}
            />
          )}
        </div>
      )}
      {(isLoading || isSubmittingCheckPoint || isPendingUpdateCheckPoint) && (
        <Loader />
      )}
    </>
  );
};
